import React, { useState } from 'react';
import { CalendarDaysIcon, ClipboardDocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { calendarFeedAPI, CalendarFeedLink } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';

/**
 * Személyes naptár-előfizetés: a klub eseményei a telefon saját naptárában
 * (Google, Apple, Outlook), automatikusan frissülve. A link csak kérésre jön
 * létre, és bármikor újra lehet generálni.
 */
const CalendarFeedCard: React.FC = () => {
  const { showToast } = useToast();
  const [link, setLink] = useState<CalendarFeedLink | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setLink(await calendarFeedAPI.getLink());
    } catch (err) {
      showToast(getErrorMessage(err, 'A naptár linket nem sikerült lekérni.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Új linket kérsz? A régi link azonnal megszűnik - ahol fel vagy iratkozva, ott újra fel kell venni.')) {
      return;
    }
    setLoading(true);
    try {
      setLink(await calendarFeedAPI.reset());
      showToast('Új link elkészült.');
    } catch (err) {
      showToast(getErrorMessage(err, 'Nem sikerült új linket kérni.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      showToast('Link a vágólapon.');
    } catch {
      showToast('A másolás nem sikerült - jelöld ki a linket és másold kézzel.', 'error');
    }
  };

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-gray-100 card-header bg-gradient-to-r from-emerald-50 to-white px-4 py-4 md:px-6">
        <div className="flex items-center space-x-3">
          <div className="card-header-icon rounded-lg bg-emerald-100 p-2">
            <CalendarDaysIcon className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Naptár a telefonodon</h2>
            <p className="text-sm text-gray-500">
              Az edzések és meccsek megjelennek a saját naptáradban, és maguktól frissülnek.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4 md:p-6">
        {!link ? (
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="w-full rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto"
          >
            {loading ? 'Betöltés...' : 'Naptár-link kérése'}
          </button>
        ) : (
          <>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                readOnly
                value={link.url}
                onFocus={(e) => e.target.select()}
                className="w-full min-w-0 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700"
              />
              <button
                type="button"
                onClick={copy}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Másolás
              </button>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <a
                href={link.webcalUrl}
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                Hozzáadás (iPhone, Mac, Outlook)
              </a>
              <a
                href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(link.webcalUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Hozzáadás Google Naptárhoz
              </a>
            </div>

            <ul className="list-disc space-y-1 pl-5 text-xs text-gray-500">
              <li>Androidon a Google Naptárhoz adás után a naptár a telefonon is megjelenik (a szinkronizálást a Google Naptár app beállításaiban kapcsold be).</li>
              <li>A naptáralkalmazások általában néhány óránként frissítenek - egy friss változás az appban azonnal, a naptárban kis késéssel látszik.</li>
              <li>A link személyes: aki ismeri, látja a naptáradat. Ha rossz kezekbe került, kérj újat.</li>
            </ul>

            <button
              type="button"
              onClick={reset}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Új link kérése (a régi megszűnik)
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarFeedCard;
